import { useMemo } from 'react';

import { useIsLoggedIn } from '../../../../context/AuthMachine';
import { useCustomer } from '../../../../context/Customer';
import {
  type CanCancelOrderV2QueryVariables,
  useCanCancelOrderV2Query,
} from '../../graphql/OrderStatus.generated';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A custom hook that returns a boolean indicating whether the given order
 * can be canceled and a helper to refetch the status.
 */
export const useCanCancelOrder = (order: OrderToCancel | undefined) => {
  const { customer } = useCustomer();

  // ─── Flags ───────────────────────────────────────────────────────────

  const isLoggedIn = useIsLoggedIn();

  // ─── Derived Data ────────────────────────────────────────────────────

  const hasRequiredInput = Boolean(customer.id && order);

  const canCancelOrderVariables = useMemo<CanCancelOrderV2QueryVariables>(() => {
    return {
      input: {
        customerId: customer.id ?? '',
        orderId: order?.id ?? '',
        restaurantId: order?.restaurant.id ?? '',
        wantedTime: order?.wantedTime ?? '',
        origin: 'customer',
      },
    };
  }, [customer.id, order]);

  // ─── Query ───────────────────────────────────────────────────────────

  const shouldPauseCanCancelQuery = !isLoggedIn || !hasRequiredInput;

  const canCancelOrderV2Query = useCanCancelOrderV2Query({
    variables: canCancelOrderVariables,
    pause: shouldPauseCanCancelQuery,
    requestPolicy: 'network-only',
  });

  const refetchCanCancelStatus = canCancelOrderV2Query?.[1];
  const resolvedData = canCancelOrderV2Query?.[0].data?.canCancelV2?.[0];

  // ─────────────────────────────────────────────────────────────────────

  const canCancel = resolvedData?.__typename === 'CanCancel' && resolvedData.canCancel;

  return {
    canCancel,
    refetchCanCancelStatus,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type OrderToCancel = Readonly<{
  id: string;
  canCancel: boolean;
  restaurant: {
    id: string;
  };
  wantedTime: string; // ISO
}>;
